import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, CalendarDays } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { fetchAdditionalExperiences } from "@/services/supabaseService";

const richTextToPlainText = (value: string) => {
  if (typeof document !== "undefined") {
    const element = document.createElement("div");
    element.innerHTML = value;
    return (element.textContent || "").replace(/\s+/g, " ").trim();
  }
  return value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
};

const sanitizeRichText = (value: string) => {
  if (typeof document === "undefined") return value;
  const documentNode = new DOMParser().parseFromString(value, "text/html");
  documentNode
    .querySelectorAll("script, style, object, embed, form")
    .forEach((element) => element.remove());
  documentNode.querySelectorAll("*").forEach((element) => {
    Array.from(element.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const unsafeUrl =
        (name === "href" || name === "src") &&
        /^\s*javascript:/i.test(attribute.value);
      if (name.startsWith("on") || unsafeUrl) element.removeAttribute(attribute.name);
    });
  });
  return documentNode.body.innerHTML;
};

type AdditionalExperiencesProps = {
  descriptionPreviewLength?: number;
  showViewMore?: boolean;
};

const AdditionalExperiences = ({
  descriptionPreviewLength,
  showViewMore = false,
}: AdditionalExperiencesProps) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const { data: experiences = [], isLoading } = useQuery({
    queryKey: ["additional-experiences"],
    queryFn: fetchAdditionalExperiences,
  });

  return (
    <div className="container max-w-6xl mx-auto py-20 px-6">
      <div className="animate-slide-down mb-10">
        <h2 className="mb-2 text-4xl font-bold text-teal">
          Campus &amp; Community Experience
        </h2>
        <p className="text-gray-400">
          Leadership, service, and community roles beyond my professional work.
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-6" aria-label="Loading experiences">
          {[0, 1].map((item) => (
            <div key={item} className="h-40 rounded-xl bg-white/5 animate-pulse" />
          ))}
        </div>
      ) : experiences.length === 0 ? (
        <div className="glass-morphism rounded-xl p-8 text-center">
          <p className="text-gray-300">
            No campus or community experiences have been published yet.
          </p>
          <p className="mt-2 text-sm text-gray-500">
            New roles will appear here after they are added from the admin dashboard.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {experiences.map((experience, index) => {
            const plainDescription = richTextToPlainText(experience.description);
            const canCollapse =
              Boolean(descriptionPreviewLength) &&
              plainDescription.length > descriptionPreviewLength!;
            const isExpanded = expandedIds.has(experience.id);
            const collapsedDescription =
              canCollapse && !isExpanded
                ? `${plainDescription.slice(0, descriptionPreviewLength).trimEnd()}…`
                : plainDescription;
            const showRichText = !canCollapse || isExpanded;

            return (
            <article
              key={experience.id}
              className="glass-morphism rounded-xl p-5 md:p-6 animate-fade-in"
              style={{ animationDelay: `${0.1 + index * 0.08}s` }}
            >
              <div className="flex items-start justify-between gap-5">
                <div className="min-w-0 flex-1">
                  <h3 className="mb-1 text-xl font-bold text-white">
                    {experience.title}
                  </h3>
                  {experience.subtitle && (
                    <p className="mb-3 text-sm font-medium italic text-gray-400">
                      {experience.subtitle}
                    </p>
                  )}
                  <div className="mb-3 flex items-center gap-2 text-sm text-teal">
                    <CalendarDays className="h-4 w-4 shrink-0" aria-hidden="true" />
                    <span>{experience.period}</span>
                  </div>
                  {showRichText ? (
                    <div
                      className="prose prose-invert max-w-none text-sm leading-relaxed text-gray-300 [&_a]:text-teal [&_a]:underline"
                      dangerouslySetInnerHTML={{ __html: sanitizeRichText(experience.description) }}
                    />
                  ) : (
                    <p className="whitespace-pre-line text-sm leading-relaxed text-gray-300">
                      {collapsedDescription}
                    </p>
                  )}
                  {canCollapse && (
                    <button
                      type="button"
                      className="mt-3 text-sm text-teal hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal"
                      aria-expanded={isExpanded}
                      onClick={() =>
                        setExpandedIds((current) => {
                          const next = new Set(current);
                          if (next.has(experience.id)) next.delete(experience.id);
                          else next.add(experience.id);
                          return next;
                        })
                      }
                    >
                      {isExpanded ? "Hide" : "Expand"}
                    </button>
                  )}
                </div>

                {experience.image_url && (
                  <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg md:h-14 md:w-14">
                    <img
                      src={experience.image_url}
                      alt=""
                      className="h-full w-full object-contain"
                      loading="lazy"
                      decoding="async"
                      width="56"
                      height="56"
                    />
                  </div>
                )}
              </div>
            </article>
            );
          })}
        </div>
      )}

      {showViewMore && experiences.length > 0 && !isLoading && (
        <div className="mt-12 flex justify-center">
          <Button asChild className="flex items-center gap-2 bg-teal text-navy-dark hover:bg-teal-dark">
            <Link to="/experience">
              View More
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
};

export default AdditionalExperiences;
